/**
 * Zod schemas + typed errors for the impersonation feature surface.
 *
 * Phase 3.1 Plan 03 (per ADR-006 section 3 + ADR-005):
 *   POST /v1/admin/impersonate/<org_id> response shape (Phase 1.5 Plan 04
 *   contract; the response carries NO refresh_token because impersonation
 *   sessions are not refreshable per the 1-hour cap).
 *
 *   POST /v1/admin/impersonate/exit response shape (idempotent 200; replays
 *   return the same body per ADR-005 section 3 step 6).
 *
 * The error classes carry typed code fields so the UI can branch without
 * reading raw HTTP status codes; Phase 7 may extend with additional codes
 * (e.g., 'reject_closed_session' from the exit-replay-after-cap-expiry path).
 */
import { z } from 'zod';
import { OrgSummarySchema } from '@/features/auth/schemas';

/**
 * Response body of POST /v1/admin/impersonate/<org_id>.
 *
 * IMPORTANT: there is no refresh_token field; impersonation tokens are
 * single-shot per ADR-005. The active refresh slot stores
 * IMPERSONATION_NO_REFRESH (auth-storage.ts) for the duration of the
 * impersonation session.
 */
export const ImpersonateStartResponseSchema = z.object({
  access_token: z.string(),
  impersonation_session_id: z.string(),
  target_org: OrgSummarySchema,
  expires_at: z.string(),
});

/** Inferred type for the impersonate-start response. */
export type ImpersonateStartResponse = z.infer<typeof ImpersonateStartResponseSchema>;

/**
 * Response body of POST /v1/admin/impersonate/exit.
 *
 * Per ADR-005 section 3 step 6 the endpoint is idempotent; replays return
 * 200 with the same impersonation_session_id and the original
 * session_closed_at timestamp.
 */
export const ImpersonateExitResponseSchema = z.object({
  ok: z.boolean(),
  impersonation_session_id: z.string(),
  session_closed_at: z.string(),
});

/** Inferred type for the impersonate-exit response. */
export type ImpersonateExitResponse = z.infer<typeof ImpersonateExitResponseSchema>;

/**
 * Typed error thrown when POST /v1/admin/impersonate/<org_id> fails or
 * the post-swap /v1/auth/me probe fails.
 *
 * The status field is the HTTP status code from the rejected fetch, or
 * 0 for transport errors (network, parse).
 */
export class StartImpersonationError extends Error {
  /** HTTP status code, or 0 for transport-level failures. */
  public readonly status: number;
  /** Short error code for UI branching. */
  public readonly code: string;

  constructor(code: string, status: number, message?: string) {
    super(message ?? code);
    this.name = 'StartImpersonationError';
    this.code = code;
    this.status = status;
  }
}

/**
 * Typed error thrown when exit-impersonation orchestration fails to
 * restore the staff session (sidecar absent, post-swap /me 401, etc.).
 *
 * Note: a non-200 from POST /v1/admin/impersonate/exit itself does NOT
 * throw this error; the exit endpoint is idempotent and the orchestrator
 * always proceeds to swap-back regardless of the exit response.
 */
export class ExitImpersonationError extends Error {
  /** Short error code for UI branching. */
  public readonly code: string;

  constructor(code: string, message?: string) {
    super(message ?? code);
    this.name = 'ExitImpersonationError';
    this.code = code;
  }
}
